import { Model } from 'app/model/backendmodel';
import { Injectable } from '@angular/core';
import { AzureResourceManagementApiService } from 'app/apis/azure-resource-management-api.service';
import { Http, Headers } from 'app/services/http';
import { AuthService } from 'app/services/authentication/auth.service';
import { FactoryUrlInformationService } from 'app/services/factory-url-information.service';
import { GlobalVariables, WORKSPACE_APIVERSION } from 'app/services/globals';

import { format } from 'app/shared/utils/string.util';
import { extractErrorMessageFromRPResponse } from 'app/shared/utils/http-response.util';
import { LoggerService } from 'app/logger/logger.service';
import { WorkspaceUrlInformationService } from 'app/services/workspace-url-information.service';
import { MetadataClientFactoryService } from 'app/services/metadata-client/metadata-client-factory.service';
import { UrlInformationBaseService } from 'app/services/url-information-base.service';
import { IMetadataClient } from 'app/services/base-metadata-client';
import { ThrottleStateManager } from 'app/shared/utils/throttle-state-manager';

@Injectable({
    providedIn: 'root'
})
export class IntegrationRuntimeOperationsService extends AzureResourceManagementApiService {

    private static readonly componentName = 'IntegrationRuntimeOperationsService';

    protected readonly _apiVersion = __IS_A365__ ? WORKSPACE_APIVERSION : GlobalVariables.dataFactoryVersion;
    protected get baseUri(): string {
        // can't put this into constructor as factory is not set at that time
        return `${this._metadataClient.getBaseUri()}/integrationruntimes`;
    }

    public get baseResourceUri(): string {
        return `${this._metadataClient.getBaseResourceUri()}`;
    }

    public get baseSubIdUri(): string {
        return `${this._metadataClient.getSubIdUri()}`;
    }

    private readonly _urlService: UrlInformationBaseService;
    private readonly _metadataClient: IMetadataClient;
    private readonly _acceptLanguage = 'Accept-Language';

    public constructor(
        readonly httpClient: Http,
        readonly authService: AuthService,
        readonly adfUrlService: FactoryUrlInformationService,
        readonly a365UrlService: WorkspaceUrlInformationService,
        private readonly _metadataClientFactory: MetadataClientFactoryService,
        private readonly _logger: LoggerService,
        throttleStateManager: ThrottleStateManager) {
        super(httpClient, authService, throttleStateManager);
        this._urlService = __IS_A365__ ? a365UrlService : adfUrlService;
        this._metadataClient = this._metadataClientFactory.getClient(false);
        this._authArea = this._metadataClient.getAuthArea();
    }

    public getResourceId(irName: string): string {
        const baseURL = this._urlService.getResourceId();
        return `/${baseURL}/integrationruntimes/${irName}`;
    }

    public createOrUpdateIntegrationRuntimeAsync(irName: string, irPayload: Model.IntegrationRuntimeResource): Promise<Model.IntegrationRuntimeResource> {
        // This request should be sent without retry as we have special handler for 503 response.
        const uri: string = `${this.baseUri}/${irName}`;
        return this.sendMessageWithoutRetryAsync<Model.IntegrationRuntimeResource, Model.IntegrationRuntimeResource>(uri, 'PUT', irPayload, undefined, this._apiVersion);
    }

    public listIntegrationRuntimesAsync(): Promise<Model.IntegrationRuntimeResource[]> {
        return this._metadataClient.listResources('integrationRuntime');
    }

    public getIntegrationRuntimeAsync(irName: string): Promise<Model.IntegrationRuntimeResource> {
        const uri: string = `${this.baseUri}/${irName}`;
        return this.sendMessageAsync<void, Model.IntegrationRuntimeResource>(uri, 'GET');
    }

    public getIntegrationRuntimeAirflowAsync(irName: string): Promise<Model.IntegrationRuntimeResource> {
        const uri: string = `${this.baseUri}/${irName}`;
        return this.sendMessageWithoutRetryAsync<void, Model.IntegrationRuntimeStatusResponse>(uri, 'GET');
    }

    public updateIntegrationRuntimesAsync(irName: string, parameters: Model.UpdateIntegrationRuntimeRequest): Promise<Model.IntegrationRuntimeStatusResponse> {
        const uri: string = `${this.baseUri}/${irName}`;
        return this.sendMessageAsync<Model.UpdateIntegrationRuntimeRequest, Model.IntegrationRuntimeStatusResponse>(uri, 'PATCH', parameters);
    }

    public getStatusAsync(irName: string): Promise<Model.IntegrationRuntimeStatusResponse> {
        const uri: string = `${this.baseUri}/${irName}/getStatus`;
        return this.sendMessageAsync<void, Model.IntegrationRuntimeStatusResponse>(uri, 'POST');
    }

    public getConnectionInfoAsync(irName: string): Promise<Model.IntegrationRuntimeConnectionInfo> {
        const uri: string = `${this.baseUri}/${irName}/getConnectionInfo`;
        return this.sendMessageAsync<void, Model.IntegrationRuntimeConnectionInfo>(uri, 'POST');
    }

    public getAirflowPropertiesAsync(irName: string): Promise<Model.IntegrationRuntimeAirflowProperties> {
        const uri: string = `${this.baseUri}/${irName}/getAirflowProperties`;
        return this.sendMessageAsync<void, Model.IntegrationRuntimeAirflowProperties>(uri, 'POST');
    }

    public getIRRegions(): Promise<Model.IntegrationRuntimeRegions> {
        const uri: string = `${this.baseResourceUri}`;
        return this.sendMessageAsync(uri, 'GET', null, null, '2018-06-01', null, null, null, false, true);
    }

    public getIRLocationWithLocale(locale: string): Promise<Model.IntegrationRuntimeLocation> {
        const uri: string = `${this.baseSubIdUri}/locations`;
        const headers = new Headers();
        headers.append(this._acceptLanguage, locale);
        return this.sendMessageAsync(uri, 'GET', null, null, '2020-01-01', null, headers, null, false, true);
    }

    public getIntegrationRuntimeExpressInstallLinkAsync(
        irName: string,
        parameters: Model.IntegrationRuntimeCreateExpressSHIRInstallLinkParameters
    ): Promise<Model.IntegrationRuntimeCreateExpressSHIRInstallLinkResponse> {
        const useExecutableFile = !this._isIEOrEdgeBrowser();
        parameters.UseExecutableFile = useExecutableFile;
        parameters.ScopeId = this._urlService.subscriptionId;
        const uri: string = `${this.baseUri}/${irName}/createExpressSHIRInstallLink`;
        return this.sendMessageAsync<Model.IntegrationRuntimeCreateExpressSHIRInstallLinkParameters, Model.IntegrationRuntimeCreateExpressSHIRInstallLinkResponse>(uri, 'POST', parameters);
    }

    public regenerateAuthKeyAsync(irName: string, parameters: Model.IntegrationRuntimeRegenerateKeyParameters): Promise<Model.IntegrationRuntimeAuthKeys> {
        const uri: string = `${this.baseUri}/${irName}/regenerateAuthKey`;
        return this.sendMessageAsync<Model.IntegrationRuntimeRegenerateKeyParameters, Model.IntegrationRuntimeAuthKeys>(uri, 'POST', parameters);
    }

    public listAuthKeysAsync(irName: string): Promise<Model.IntegrationRuntimeAuthKeys> {
        const uri: string = `${this.baseUri}/${irName}/listAuthKeys`;
        return this.sendMessageAsync<void, Model.IntegrationRuntimeAuthKeys>(uri, 'POST');
    }

    public startAsync(irName: string): Promise<Response> {
        // TODO Response type changed to Model.IntegrationRuntimeStatusResponse
        const uri: string = `${this.baseUri}/${irName}/start`;
        return this.sendMessageGetFullResponseAsync<void>(uri, 'POST');
    }

    public stopAsync(irName: string): Promise<Response> {
        // TODO Response type changed to Model.IntegrationRuntimeStatusResponse
        const uri: string = `${this.baseUri}/${irName}/stop`;
        return this.sendMessageGetFullResponseAsync<void>(uri, 'POST');
    }

    public syncCredentialsAsync(irName: string): Promise<Response> {
        const uri: string = `${this.baseUri}/${irName}/syncCredentials`;
        return this.sendMessageGetFullResponseAsync<void>(uri, 'POST');
    }

    public monitoringDataAsync(irName: string): Promise<Model.IntegrationRuntimeMonitoringData> {
        const uri: string = `${this.baseUri}/${irName}/monitoringData`;
        return this.sendMessageAsync<void, Model.IntegrationRuntimeMonitoringData>(uri, 'POST');
    }

    public upgradeAsync(irName: string): Promise<Response> {
        const uri: string = `${this.baseUri}/${irName}/upgrade`;
        return this.sendMessageGetFullResponseAsync<void>(uri, 'POST');
    }

    public deleteNodeAsync(irName: string, nodeName: string): Promise<Response> {
        const uri: string = `${this.baseUri}/${irName}/nodes/${nodeName}`;
        return this.sendMessageGetFullResponseAsync<void>(uri, 'DELETE');
    }

    public updateNodeAsync(irName: string, nodeName: string, parameters: Model.UpdateIntegrationRuntimeNodeRequest): Promise<Model.SelfHostedIntegrationRuntimeNode> {
        const uri: string = `${this.baseUri}/${irName}/nodes/${nodeName}`;
        return this.sendMessageAsync<Model.UpdateIntegrationRuntimeNodeRequest, Model.SelfHostedIntegrationRuntimeNode>(uri, 'PATCH', parameters);
    }

    public getIpAddressAsync(irName: string, nodeName: string): Promise<Model.IntegrationRuntimeNodeIpAddress> {
        const uri: string = `${this.baseUri}/${irName}/nodes/${nodeName}/ipAddress`;
        return this.sendMessageAsync<void, Model.IntegrationRuntimeNodeIpAddress>(uri, 'POST');
    }

    public removeLinksAsync(irName: string, sharedDataFactoryName: string): Promise<Response> {
        const body: Model.LinkedIntegrationRuntimeRequest = {
            factoryName: sharedDataFactoryName
        };
        const uri: string = `${this.baseUri}/${irName}/removeLinks`;
        return this.sendMessageGetFullResponseAsync<Model.LinkedIntegrationRuntimeRequest>(uri, 'POST', body);
    }

    public enableInteractiveQuery(irName: string, autoTerminateMinutes: number): Promise<Response> {
        const body: Model.EnableIntegrationRuntimeInteractiveQueryRequest = {
            autoTerminationMinutes: autoTerminateMinutes
        };
        const uri: string = `${this.baseUri}/${irName}/enableInteractiveQuery`;
        return this.sendMessageGetFullResponseAsync<Model.EnableIntegrationRuntimeInteractiveQueryRequest>(uri, 'POST', body, undefined, undefined, true);
    }

    public disableInteractiveQuery(irName: string): Promise<Response> {
        const uri: string = `${this.baseUri}/${irName}/disableInteractiveQuery`;
        return this.sendMessageGetFullResponseAsync<void>(uri, 'POST', undefined, undefined, undefined, true);
    }

    public async refreshSsisObjectMetadataAndWaitForCompleteAsync(irName: string, checkOperationRetryCount: number = 3, timeoutInSecond: number = 20 * 60): Promise<Model.OperationStatusResponse> {
        const operationUrl = await this.refreshSsisObjectMetadataAsync(irName);
        const timer = this._timer();
        let retryCount = 0;
        const sleepInMillisecond = 3000;
        return new Promise<Model.OperationStatusResponse>((resolve, reject) => {
            const checkOperationFun = async () => {
                if (timer.seconds > timeoutInSecond) {
                    const error = new Error(format(stringResources.SsisActivityRefreshMetadataTimeout, timeoutInSecond.toString()));
                    this._logger.error(IntegrationRuntimeOperationsService.componentName, error);
                    reject(error);
                    return;
                }
                try {
                    const response = await this.sendMessageWithoutVersionAsync<void, Model.OperationStatusResponse>(operationUrl, 'GET');
                    if (response.status !== 'InProgress') {
                        if (response.status === 'Succeeded') {
                            resolve(response);
                        } else {
                            this._logger.error(IntegrationRuntimeOperationsService.componentName, response);
                            reject(response);
                        }
                        return;
                    } else {
                        setTimeout(() => checkOperationFun(), sleepInMillisecond);
                    }
                } catch (e) {
                    if (retryCount++ > checkOperationRetryCount) {
                        const error = new Error(format(stringResources.SsisActivityGetRefreshOperationFailed, checkOperationRetryCount.toString(), extractErrorMessageFromRPResponse(e)));
                        this._logger.error(IntegrationRuntimeOperationsService.componentName, error);
                        reject(error);
                        return;
                    } else {
                        this._logger.warn(IntegrationRuntimeOperationsService.componentName, e);
                        setTimeout(() => checkOperationFun(), sleepInMillisecond);
                    }
                }
            };
            setTimeout(() => checkOperationFun(), sleepInMillisecond);
        });
    }

    public async refreshSsisObjectMetadataAsync(irName: string): Promise<string> {
        const uri: string = `${this.baseUri}/${irName}/refreshObjectMetadata`;
        const response = await this.sendMessageGetFullResponseAsync<void>(uri, 'POST');
        if (!response.ok) {
            throw new Error(extractErrorMessageFromRPResponse(response));
        }
        return response.headers.get('Azure-AsyncOperation');
    }

    public async getSsisObjectMetadataAsync(irName: string, request: Model.GetSsisObjectMetadataRequest): Promise<Model.SsisObjectMetadataListResponse> {
        const uri: string = `${this.baseUri}/${irName}/getObjectMetadata`;
        let response = await this.sendMessageAsync<Model.GetSsisObjectMetadataRequest, Model.SsisObjectMetadataListResponse>(uri, 'POST', request);
        const metadataList: Model.SsisObjectMetadata[] = [];
        metadataList.push(...response.value);
        let nextLink = response.nextLink;
        while (nextLink && response.value && response.value.length > 0) {
            if (__IS_A365__) {
                const baseUri = new URL(uri);
                const nextUri = new URL(nextLink);
                nextUri.host = baseUri.host;
                nextUri.port = baseUri.port;
                nextLink = nextUri.href;
            }
            response = await this.sendMessageWithoutVersionAsync<Model.GetSsisObjectMetadataRequest, Model.SsisObjectMetadataListResponse>(nextLink, 'POST');
            metadataList.push(...response.value);
            nextLink = response.nextLink;
        }
        return { value: metadataList };

    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _timer(): any {
        const timeStart = new Date().getTime();
        return {
            get seconds(): number {
                return Math.ceil((new Date().getTime() - timeStart) / 1000);
            }
        };
    }

    private _isIEOrEdgeBrowser(): boolean {
        const agent = window.navigator.userAgent;
        return agent.indexOf('Edge') >= 0 || agent.indexOf('Trident') >= 0 || agent.indexOf('MSIE') >= 0;
    }
}
